import {
  Editable,
  EditableInput,
  EditablePreview,
  useEditableControls,
} from '@chakra-ui/react';
import { IconPencilLine } from 'central-icons/IconPencilLine';
import { useRef } from 'react';

import { Divider, IconButton, Text } from '@revelio/ui';

import { useHideNav } from '../../../shared/hooks/useHideNav';
import {
  GlobalTopRightContent,
  PoweredByRevelioLabsLogo,
} from '../../GlobalTopRightContent';
import { getPrimaryFilterLabel } from '../filters/utils/common';
import {
  type AnalyticsEntity,
  getAnalyticsEntityForPrimaryFilter,
  getAnalyticsEntityFromRevelioPageId,
} from '../utils/revelio-primary-filter-entity-switch';
import {
  getPrimaryFilterIdForScreenerV2Page,
  isScreenerV2Page,
} from '../utils/routing-helpers';
import { AnalyticsViewTabs } from './AnalyticsViewTabs';
import { useEditMode } from './EditModeProvider/EditModeProvider';
import styles from './Header.module.css';
import { PageActionBar } from './PageActionBar/PageActionBar';
import {
  usePageConfig,
  useTabContentConfig,
} from './PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from './PageConfigProvider/types';

export const Header = ({
  isReportPage = false,
}: {
  isReportPage?: boolean;
}) => {
  const { config: fullConfig, dispatch: fullConfigDispatch } = usePageConfig();
  const { name, revelioPageId } = fullConfig;

  const { config: tabContent } = useTabContentConfig();

  const hideNav = useHideNav();
  const analyticsEntity = getAnalyticsEntityFromRevelioPageId(revelioPageId);

  const primaryFilterId =
    tabContent.primaryFilter?.id ??
    getPrimaryFilterIdForScreenerV2Page(revelioPageId);

  // Show the tab bar on custom-dash pages too (not just analytics views) so
  // users can hop between any tab without leaving the header. Discovery /
  // screener / report pages still opt out.
  const customPageEntity: AnalyticsEntity | undefined =
    analyticsEntity || revelioPageId || isReportPage
      ? undefined
      : getAnalyticsEntityForPrimaryFilter(primaryFilterId);
  const tabBarEntity = analyticsEntity ?? customPageEntity;
  const showTabBar = Boolean(tabBarEntity) && !hideNav;

  const primaryFilterLabel = (() => {
    if (primaryFilterId) return getPrimaryFilterLabel(primaryFilterId);
    if (revelioPageId === RevelioPageId.TALENT_DISCOVERY) return 'Individual';
    if (revelioPageId === RevelioPageId.POSTING_DISCOVERY) return 'Postings';
    return null;
  })();

  const isDiscoveryPage =
    revelioPageId === RevelioPageId.TALENT_DISCOVERY ||
    revelioPageId === RevelioPageId.POSTING_DISCOVERY ||
    isScreenerV2Page(revelioPageId);

  const { editMode } = useEditMode();

  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (value: string) => {
    const finalTitle = value.trim() || 'Untitled';

    if (finalTitle !== name) {
      fullConfigDispatch({ type: 'UPDATE_PAGE_TITLE', payload: finalTitle });
    }
  };

  const titleNode = (() => {
    if (analyticsEntity) {
      return (
        <Text className={styles.pageTitle}>
          {`${primaryFilterLabel ?? ''} Dashboards`.trim()}
        </Text>
      );
    }
    if (isDiscoveryPage && primaryFilterLabel) {
      return (
        <Text className={styles.pageTitle}>
          {`${primaryFilterLabel} Discovery`}
        </Text>
      );
    }
    return (
      <div className={styles.titleGroup} data-edit-mode={editMode || undefined}>
        {primaryFilterLabel && (
          <>
            <Text className={`${styles.pageTitle} ${styles.titlePrefix}`}>
              {`${primaryFilterLabel ?? ''} Dashboards`.trim()}
            </Text>
            <svg
              className={styles.chevron}
              width={20}
              height={20}
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                fill="currentColor"
                d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"
              />
            </svg>
          </>
        )}
        {editMode ? (
          <Editable
            key={name}
            defaultValue={name}
            onSubmit={handleSubmit}
            submitOnBlur
            className={styles.editableTitle}
          >
            <EditablePreview
              className={`${styles.pageTitle} ${styles.editablePreview}`}
            />
            <EditableInput
              ref={inputRef}
              className={styles.titleInput}
              size={Math.max(name.length + 2, 24)}
            />
            <EditButton />
          </Editable>
        ) : (
          <Text className={`${styles.pageTitle} ${styles.titleTruncate}`}>
            {name}
          </Text>
        )}
      </div>
    );
  })();

  return (
    <>
      <div
        role="group"
        className={styles.headerRow}
        data-testid="page-header-label"
      >
        {titleNode}
        <div className={styles.spacer} />
        {tabBarEntity && showTabBar && !editMode && (
          <>
            <AnalyticsViewTabs entity={tabBarEntity} />
            <Divider
              className={`${styles.divider} ${styles.headerDivider}`}
              orientation="vertical"
            />
          </>
        )}
        <GlobalTopRightContent
          showPoweredBy={!tabBarEntity}
          className={styles.topRightCluster}
        >
          <PageActionBar
            isReportPage={isReportPage}
            hideEditButton={showTabBar}
          />
        </GlobalTopRightContent>
      </div>
      {tabBarEntity && (
        <div className={styles.poweredByRow}>
          <PoweredByRevelioLabsLogo />
        </div>
      )}
    </>
  );
};

const EditButton = () => {
  const { isEditing, getEditButtonProps } = useEditableControls();

  if (isEditing) return null;

  return (
    <IconButton
      size="sm"
      icon={<IconPencilLine size={16} />}
      aria-label="Edit page name"
      variant="ghost"
      className={styles.editButton}
      {...getEditButtonProps()}
    />
  );
};
